import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  useMyProjects,
  useAvailableLeads,
  useAcceptLead,
  useDeclineLead,
  useNotifications,
  useInstallerProfile,
  useProjectDocuments,
  useProjectMessages,
  useSendMessage,
  useMarkNotificationRead,
  useUpdateProjectStage,
  useUpdateInstallerProfile,
  useInstallerPack,
  useSaveInstallerNote,
  useInstallerSurveyList,
  useAttachSuggestions,
  useAttachSurvey,
  useInstallerAvailability,
  useUpdateInstallerAvailability,
} from "@/api/hooks";
import type { InstallerProfile, InstallerSurveyListItem } from "@/api/types";
import { useAuth } from "@/_core/hooks/useAuth";
import { LoginPrompt } from "@/components/auth/AuthDialog";
import { HelioHomeLink } from "@/components/HelioLogoMark";
import PortalSwitcher from "@/components/PortalSwitcher";
import { TRADING_NAME } from "@/constants/company";
import { PORTAL_MESSAGING_ENABLED } from "@/constants/projectStages";
import {
  dnoBadgeClass,
  projectMatchesDnoFilter,
  type DnoJobFilter,
} from "@/constants/dnoStatus";
import { API_BASE } from "@/constants/api";
import { getAuthToken, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { openSignedDocument } from "@/utils/documents";
import { formatDateOnlyLabel } from "@/utils/dateOnly";
import { InstallAvailabilityCalendar } from "@/components/availability/InstallAvailabilityCalendar";
import CoverageAreaMap from "@/components/coverage-map/CoverageAreaMap";
import { isAreaToken, parseCoverageTokens, toggleAreaToken } from "@/utils/coverageAreas";
import {
  Sun, Briefcase, CheckCircle, Clock, MapPin, User, Phone, Mail,
  MessageSquare, FileText, Bell, LogOut, ChevronRight, Loader2,
  Send, Download, Upload, Settings, BarChart3, Calendar, Zap,
  Battery, Car, TrendingUp, ArrowRight, X, Check, Inbox, XCircle, ClipboardList
} from "lucide-react";
import { motion } from "framer-motion";
import { formatRelativeTime } from "@/utils/date";

// ─── Stage badge ──────────────────────────────────────────────────────────────
function DnoBadge({ label, status }: { label: string; status?: string | null }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide ${dnoBadgeClass(status)}`}
      title="DNO application"
    >
      DNO: {label}
    </span>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const cls: Record<string, string> = {
    lead: "bg-white/10 text-white/60",
    quoted: "bg-[#3d8eff]/20 text-[#3d8eff]",
    approved: "bg-brand-lime/20 text-brand-lime",
    surveyed: "bg-purple-500/20 text-purple-400",
    scheduled: "bg-[#3d8eff]/20 text-[#3d8eff]",
    installing: "bg-orange-500/20 text-orange-400",
    complete: "bg-brand-lime/20 text-brand-lime",
    aftercare: "bg-white/10 text-white/60",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${cls[stage] ?? "bg-white/10 text-white/60"}`}>
      {stage}
    </span>
  );
}

// ─── Stage update modal ───────────────────────────────────────────────────────
const STAGE_OPTIONS = ["lead", "quoted", "approved", "surveyed", "scheduled", "installing", "complete", "aftercare"] as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function StageUpdateModal({ project, onSuccess }: { project: any; onSuccess: () => void }) {
  const [stage, setStage] = useState(project.stage);
  const [surveyDate, setSurveyDate] = useState("");
  const [installDate, setInstallDate] = useState("");
  const [surveyNotes, setSurveyNotes] = useState("");
  const [open, setOpen] = useState(false);

  const updateStage = useUpdateProjectStage();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="btn-cta">
          Update stage
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0d1b2e] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Update project stage</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-white/70 text-sm mb-2 block">New stage</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="bg-[#132035] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1b2e] border-white/10">
                {STAGE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} className="text-white hover:bg-white/10 capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(stage === "surveyed" || stage === "scheduled") && (
            <div>
              <Label className="text-white/70 text-sm mb-2 block">
                {stage === "surveyed" ? "Survey date" : "Installation date"}
              </Label>
              <Input
                type="date"
                value={stage === "surveyed" ? surveyDate : installDate}
                onChange={(e) => stage === "surveyed" ? setSurveyDate(e.target.value) : setInstallDate(e.target.value)}
                className="bg-[#132035] border-white/10 text-white"
              />
            </div>
          )}
          {stage === "surveyed" && (
            <div>
              <Label className="text-white/70 text-sm mb-2 block">Survey notes</Label>
              <Textarea
                value={surveyNotes}
                onChange={(e) => setSurveyNotes(e.target.value)}
                className="bg-[#132035] border-white/10 text-white resize-none"
                rows={3}
                placeholder="Roof condition, shading notes, panel placement..."
              />
            </div>
          )}
          <Button
            className="btn-cta w-full"
            onClick={() => updateStage.mutate({
              id: project.id,
              stage,
              surveyDate: surveyDate || undefined,
              installDate: installDate || undefined,
              surveyNotes: surveyNotes || undefined,
            }, {
              onSuccess: () => { setOpen(false); onSuccess(); toast.success("Stage updated"); },
              onError: () => toast.error("Failed to update stage"),
            })}
            disabled={updateStage.isPending}
          >
            {updateStage.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Confirm update
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Installer Portal ────────────────────────────────────────────────────
export default function InstallerPortal() {
  const [, navigate] = useLocation();
  const { user, loading, isAuthenticated, logout } = useAuth();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [messageText, setMessageText] = useState("");
  const [activeTab, setActiveTab] = useState("jobs");
  const [leadDateFilter, setLeadDateFilter] = useState<string>("all");
  const [dnoJobFilter, setDnoJobFilter] = useState<DnoJobFilter>("all");
  const isStaff = user?.role === "admin";

  const projectsRefetchInterval =
    isAuthenticated && activeTab === "jobs" ? 60_000 : false;
  const { data: projects, isLoading: projectsLoading } = useMyProjects(
    isAuthenticated,
    "installer",
    projectsRefetchInterval,
  );
  const {
    data: availableLeads,
    isLoading: leadsLoading,
    isError: leadsError,
    error: leadsErrorDetail,
  } = useAvailableLeads(isAuthenticated && !isStaff);
  const { data: notifications } = useNotifications(isAuthenticated);
  const { data: profile, isLoading: profileLoading } = useInstallerProfile(isAuthenticated && !isStaff);
  const { data: installerAvailability } = useInstallerAvailability(isAuthenticated && !isStaff);
  const acceptLead = useAcceptLead();
  const declineLead = useDeclineLead();

  const selectedProject = projects?.find((p) => p.id === selectedProjectId) ?? projects?.[0];

  const { data: messages, refetch: refetchMessages } = useProjectMessages(
    selectedProject?.id ?? 0,
    PORTAL_MESSAGING_ENABLED && !!selectedProject,
  );
  const { data: documents } = useProjectDocuments(selectedProject?.id ?? 0, !!selectedProject);

  const sendMessageMutation = useSendMessage(selectedProject?.id ?? 0);
  const markRead = useMarkNotificationRead();

  useEffect(() => {
    if (isStaff && (activeTab === "leads" || activeTab === "profile" || activeTab === "availability")) {
      setActiveTab("jobs");
    }
  }, [isStaff, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1F3B] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-lime animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0B1F3B] text-white">
        <LoginPrompt message="Sign in to manage your jobs and update project stages." />
      </div>
    );
  }

  const unreadCount = notifications?.filter((n) => !n.readAt).length ?? 0;
  const activeJobs = projects?.filter((p) => !["complete", "aftercare"].includes(p.stage)) ?? [];
  const completedJobs = projects?.filter((p) => ["complete", "aftercare"].includes(p.stage)) ?? [];
  const filteredJobs =
    projects?.filter((p) => projectMatchesDnoFilter(p.dnoStatus, dnoJobFilter)) ?? [];
  const dnoFilterChips: { key: DnoJobFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "needs_you", label: "Needs you" },
    { key: "with_dno", label: "With DNO" },
    { key: "approved", label: "Approved" },
  ];
  const leads = Array.isArray(availableLeads) ? availableLeads : [];
  const dateChips = Array.from(
    new Set(leads.map((lead) => lead.customerAvailableDate).filter((value): value is string => Boolean(value))),
  ).sort();
  const filteredLeads = leads.filter((lead) => {
    if (leadDateFilter === "all") return true;
    if (leadDateFilter === "unset") return !lead.customerAvailableDate;
    return lead.customerAvailableDate === leadDateFilter;
  });
  const leadCount = leads.length;
  const coverageSet = Boolean(profile?.coveragePostcodes?.trim());
  const leadsErrorMessage =
    leadsErrorDetail instanceof ApiError
      ? leadsErrorDetail.message
      : "Could not load available leads.";

  const handleAcceptLead = async (leadId: number) => {
    try {
      const project = await acceptLead.mutateAsync(leadId);
      toast.success("Lead accepted. It is now in your jobs list.");
      setSelectedProjectId(project.id);
      setActiveTab("jobs");
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not accept this lead.";
      toast.error(message);
    }
  };

  const handleDeclineLead = async (leadId: number) => {
    try {
      await declineLead.mutateAsync(leadId);
      toast.success("Lead declined. It will stay available for other installers.");
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not decline this lead.";
      toast.error(message);
    }
  };

  const handleNotificationClick = (n: { id: number; type: string; readAt?: string | null }) => {
    if (!n.readAt) markRead.mutate(n.id);
    if (n.type === "new_lead" && !isStaff) setActiveTab("leads");
    if (n.type === "lead_assigned") setActiveTab("jobs");
  };

  const navItems = [
    ...(!isStaff ? [{ icon: Inbox, label: "Leads", tab: "leads", count: leadCount }] : []),
    { icon: Briefcase, label: "Jobs", tab: "jobs", count: activeJobs.length },
    ...(!isStaff ? [{ icon: Calendar, label: "Availability", tab: "availability" }] : []),
    { icon: ClipboardList, label: "Surveys", tab: "surveys" },
    ...(PORTAL_MESSAGING_ENABLED ? [{ icon: MessageSquare, label: "Messages", tab: "messages" }] : []),
    { icon: FileText, label: "Documents", tab: "documents" },
    { icon: Bell, label: "Notifications", tab: "notifications", count: unreadCount },
    ...(!isStaff ? [{ icon: Settings, label: "Profile", tab: "profile" }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#0B1F3B] text-white">
      <div className="flex h-screen overflow-hidden">
        {/* ─── Sidebar ─── */}
        <aside className="w-64 shrink-0 border-r border-white/8 bg-[#0a1628] flex flex-col">
          <div className="flex items-center gap-2 px-6 py-5 border-b border-white/8">
            <HelioHomeLink className="flex items-center gap-2">
              {/* <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-lime">
                <Sun className="h-3.5 w-3.5 text-[#050d1a]" />
              </div> */}
              <img src="/powlo-logo-lime.svg" alt="Powlo" className="h-7 w-auto" />
            </HelioHomeLink>
            <Badge className={`ml-auto text-xs ${isStaff ? "bg-red-500/10 text-red-400 border-red-500/30" : "bg-[#3d8eff]/10 text-[#3d8eff] border-[#3d8eff]/30"}`}>
              {isStaff ? "Admin" : "Installer"}
            </Badge>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map(({ icon: Icon, label, tab, count }) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === "surveys") {
                    navigate("/installer/surveys");
                    return;
                  }
                  setActiveTab(tab);
                }}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  activeTab === tab
                    ? "bg-brand-lime/10 text-brand-lime"
                    : "text-white/60 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left">{label}</span>
                {count !== undefined && count > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-lime text-[10px] font-bold text-[#050d1a] px-1">
                    {count}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="border-t border-white/8 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-8 w-8 rounded-full bg-[#3d8eff]/20 flex items-center justify-center text-[#3d8eff] text-xs font-bold">
                {user?.name?.[0] ?? "I"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name ?? (isStaff ? "Admin" : "Installer")}</p>
                <p className="text-xs text-white/40 truncate">
                  {isStaff ? "Staff" : (profile?.companyName ?? "No company set")}
                </p>
              </div>
            </div>
            <PortalSwitcher />
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </aside>

        {/* ─── Main ─── */}
        <main className="flex-1 overflow-y-auto">
          {/* Top bar */}
          <div className="sticky top-0 z-30 border-b border-white/8 bg-[#0B1F3B]/80 backdrop-blur-xl px-8 py-4 flex items-center justify-between">
            <div>
              <h1 className="font-display text-xl font-semibold capitalize">{activeTab}</h1>
              <p className="text-sm text-white/40">
                {activeTab === "jobs" ? `${activeJobs.length} active · ${completedJobs.length} complete` : ""}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 hover:bg-white/5 transition-colors"
                onClick={() => setActiveTab("notifications")}
              >
                <Bell className="h-4 w-4 text-white/60" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-lime text-[10px] font-bold text-[#050d1a]">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="p-8">
            {/* ── Leads tab ── */}
            {activeTab === "leads" && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-display font-semibold text-lg">Available leads</h2>
                  <p className="text-sm text-white/40 mt-1">
                    Quotes in your coverage area that have not been assigned yet. Dated leads only appear when you are free that day.
                  </p>
                </div>
                {dateChips.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setLeadDateFilter("all")}
                      className={`rounded-full px-3 py-1 text-xs ${leadDateFilter === "all" ? "bg-brand-lime text-[#050d1a]" : "bg-white/10 text-white/60"}`}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setLeadDateFilter("unset")}
                      className={`rounded-full px-3 py-1 text-xs ${leadDateFilter === "unset" ? "bg-brand-lime text-[#050d1a]" : "bg-white/10 text-white/60"}`}
                    >
                      Date not set
                    </button>
                    {dateChips.map((date) => (
                      <button
                        type="button"
                        key={date}
                        onClick={() => setLeadDateFilter(date)}
                        className={`rounded-full px-3 py-1 text-xs ${leadDateFilter === date ? "bg-brand-lime text-[#050d1a]" : "bg-white/10 text-white/60"}`}
                      >
                        {formatDateOnlyLabel(date)}
                      </button>
                    ))}
                  </div>
                )}
                {leadsLoading || (profileLoading && leadCount === 0 && !leadsError) ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 text-brand-lime animate-spin" />
                  </div>
                ) : leadsError ? (
                  <div className="text-center py-16 rounded-xl border border-white/8 bg-[#0d1b2e]">
                    <Inbox className="h-10 w-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40 mb-2">Could not load leads.</p>
                    <p className="text-xs text-white/30 px-6">{leadsErrorMessage}</p>
                  </div>
                ) : leadCount > 0 ? (
                  <div className="rounded-xl border border-white/8 bg-[#0d1b2e] overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                      <h3 className="font-semibold">Inbox</h3>
                      <Badge className="bg-white/10 text-white/60">{filteredLeads.length} waiting</Badge>
                    </div>
                    <div className="divide-y divide-white/8">
                      {filteredLeads.length === 0 ? (
                        <p className="px-6 py-8 text-sm text-white/40">No leads match this date filter.</p>
                      ) : filteredLeads.map((lead) => (
                        <motion.div
                          key={lead.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center"
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-lime/10">
                            <MapPin className="h-5 w-5 text-brand-lime" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="font-medium text-sm">{lead.reference}</p>
                              <StageBadge stage={lead.stage} />
                              {lead.selectedTier && (
                                <span className="text-xs text-white/40 capitalize">{lead.selectedTier}</span>
                              )}
                            </div>
                            <p className="text-xs text-white/50 truncate">
                              {lead.address ?? lead.lead?.address ?? "Address not set"}
                              {lead.postcode || lead.lead?.postcode ? ` · ${lead.postcode ?? lead.lead?.postcode}` : ""}
                            </p>
                            <p className="text-xs text-white/40 mt-0.5">
                              £{Number(lead.installerPayout ?? 0).toLocaleString("en-GB")} payout
                              {lead.systemSizeKw ? ` · ${lead.systemSizeKw} kWp` : ""}
                              {" · "}
                              {lead.customerAvailableDate
                                ? `Customer available ${formatDateOnlyLabel(lead.customerAvailableDate)}`
                                : "Date not set"}
                            </p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <button
                              type="button"
                              className="btn-cta btn-cta-primary text-sm px-4 py-2"
                              disabled={acceptLead.isPending || declineLead.isPending}
                              onClick={() => void handleAcceptLead(lead.id)}
                            >
                              {acceptLead.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                              Accept
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70 hover:bg-white/5 transition-colors"
                              disabled={acceptLead.isPending || declineLead.isPending}
                              onClick={() => void handleDeclineLead(lead.id)}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                              Decline
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ) : !coverageSet ? (
                  <div className="text-center py-16 rounded-xl border border-white/8 bg-[#0d1b2e]">
                    <Inbox className="h-10 w-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40 mb-4">Set your coverage postcodes to see leads in your area.</p>
                    <Button className="btn-cta" onClick={() => setActiveTab("profile")}>
                      Open profile
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-16 rounded-xl border border-white/8 bg-[#0d1b2e]">
                    <Inbox className="h-10 w-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40">No leads in your coverage area right now. Add availability dates so dated leads can match you.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "availability" && (
              <InstallerAvailabilityPanel dates={installerAvailability?.dates ?? []} />
            )}

            {/* ── Jobs tab ── */}
            {activeTab === "jobs" && (
              <div className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: "Active jobs", value: activeJobs.length, icon: Briefcase, color: "text-brand-lime" },
                    { label: "Completed", value: completedJobs.length, icon: CheckCircle, color: "text-[#3d8eff]" },
                    { label: "Surveys due", value: projects?.filter((p) => p.stage === "approved").length ?? 0, icon: Calendar, color: "text-orange-400" },
                    { label: "Installs this week", value: projects?.filter((p) => p.stage === "scheduled").length ?? 0, icon: Zap, color: "text-purple-400" },
                  ].map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className="rounded-xl border border-white/8 bg-[#0d1b2e] p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`h-4 w-4 ${color}`} />
                        <span className="text-xs text-white/40">{label}</span>
                      </div>
                      <p className="font-display text-2xl font-bold">{value}</p>
                    </div>
                  ))}
                </div>

                {projects && projects.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {dnoFilterChips.map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setDnoJobFilter(key)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                          dnoJobFilter === key
                            ? "border-brand-lime bg-brand-lime/10 text-brand-lime"
                            : "border-white/10 text-white/50 hover:border-white/20"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Job list */}
                {projectsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 text-brand-lime animate-spin" />
                  </div>
                ) : !projects?.length ? (
                  <div className="text-center py-16 rounded-xl border border-white/8 bg-[#0d1b2e]">
                    <Briefcase className="h-10 w-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40">No jobs assigned yet. Contact your {TRADING_NAME} account manager.</p>
                  </div>
                ) : filteredJobs.length === 0 ? (
                  <div className="text-center py-16 rounded-xl border border-white/8 bg-[#0d1b2e]">
                    <Briefcase className="h-10 w-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40">No jobs match this DNO filter.</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-white/8 bg-[#0d1b2e] overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                      <h2 className="font-semibold">All jobs</h2>
                      <Badge className="bg-white/10 text-white/60">
                        {filteredJobs.length}
                        {dnoJobFilter !== "all" ? ` of ${projects.length}` : " total"}
                      </Badge>
                    </div>
                    <div className="divide-y divide-white/8">
                      {filteredJobs.map((project) => (
                        <motion.div
                          key={project.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`flex items-center gap-4 px-6 py-4 cursor-pointer transition-colors hover:bg-white/5 ${selectedProjectId === project.id ? "bg-brand-lime/5 border-l-2 border-brand-lime" : ""}`}
                          onClick={() => setSelectedProjectId(project.id)}
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-lime/10">
                            <Sun className="h-5 w-5 text-brand-lime" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-0.5">
                              <p className="font-medium text-sm">{project.reference}</p>
                              <StageBadge stage={project.stage} />
                              <DnoBadge
                                label={project.dnoStatusLabel ?? "Not started"}
                                status={project.dnoStatus}
                              />
                            </div>
                            <p className="text-xs text-white/40 truncate">{project.address ?? "Address not set"}</p>
                          </div>
                          <div className="text-right shrink-0 flex flex-col items-end gap-2">
                            {project.installDate && (
                              <p className="text-xs text-white/40">
                                {new Date(project.installDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                              </p>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 border-white/15 text-white/80 hover:bg-white/10 hover:text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/installer/dno/${project.id}/review`);
                              }}
                            >
                              <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                              DNO
                            </Button>
                            <StageUpdateModal project={project} onSuccess={() => {}} />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected project detail */}
                {selectedProject && (
                  <div className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
                    <div className="flex items-start justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-display font-semibold text-lg mb-1">{selectedProject.reference}</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <StageBadge stage={selectedProject.stage} />
                          <DnoBadge
                            label={selectedProject.dnoStatusLabel ?? "Not started"}
                            status={selectedProject.dnoStatus}
                          />
                        </div>
                        {selectedProject.dnoApplicationId && (
                          <p className="text-xs text-white/40 mt-2">
                            ENA application {selectedProject.dnoApplicationId}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        className="btn-cta shrink-0"
                        onClick={() => navigate(`/installer/dno/${selectedProject.id}/review`)}
                      >
                        <ClipboardList className="h-4 w-4 mr-2" />
                        Review DNO application
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <h4 className="text-xs text-white/40 uppercase tracking-wider">Customer details</h4>
                        <div className="space-y-2">
                          {[
                            { icon: User, label: selectedProject.customerName ?? "—" },
                            { icon: Mail, label: selectedProject.customerEmail ?? "—" },
                            { icon: Phone, label: selectedProject.customerPhone ?? "—" },
                            { icon: MapPin, label: selectedProject.address ?? "—" },
                          ].map(({ icon: Icon, label }) => (
                            <div key={label} className="flex items-center gap-2 text-sm text-white/60">
                              <Icon className="h-4 w-4 text-white/30 shrink-0" />
                              {label}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h4 className="text-xs text-white/40 uppercase tracking-wider">System details</h4>
                        <div className="space-y-2">
                          {[
                            { icon: Zap, label: `${selectedProject.systemSizeKw ?? "—"}kWp solar` },
                            { icon: Battery, label: selectedProject.batteryKwh && Number(selectedProject.batteryKwh) > 0 ? `${selectedProject.batteryKwh}kWh battery` : "No battery" },
                            { icon: Car, label: selectedProject.includesEv ? "EV charger included" : "No EV charger" },
                            { icon: TrendingUp, label: `£${Number(selectedProject.installerPayout ?? 0).toLocaleString("en-GB")} your payout` },
                          ].map(({ icon: Icon, label }) => (
                            <div key={label} className="flex items-center gap-2 text-sm text-white/60">
                              <Icon className="h-4 w-4 text-white/30 shrink-0" />
                              {label}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {selectedProject.surveyNotes && (
                      <div className="mt-4 rounded-lg bg-white/5 p-3">
                        <p className="text-xs text-white/40 mb-1">Survey notes</p>
                        <p className="text-sm text-white/70">{selectedProject.surveyNotes}</p>
                      </div>
                    )}
                    <InstallerPackPanel
                      projectId={selectedProject.id}
                      reference={selectedProject.reference}
                      enabled={Boolean(selectedProject.hasInstallerPack || selectedProject.remoteSurveyStatus === "submitted" || selectedProject.remoteSurveyStatus === "installer_review")}
                    />
                    {!selectedProject.hasInstallerPack && (
                      <AttachSurveyButton projectId={selectedProject.id} />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Messages tab ── */}
            {PORTAL_MESSAGING_ENABLED && activeTab === "messages" && (
              <div className="space-y-4">
                {/* Project selector */}
                {projects && projects.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {projects.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedProjectId(p.id)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                          (selectedProjectId ?? projects[0].id) === p.id
                            ? "border-brand-lime bg-brand-lime/10 text-brand-lime"
                            : "border-white/10 text-white/50 hover:border-white/20"
                        }`}
                      >
                        {p.reference}
                      </button>
                    ))}
                  </div>
                )}

                <div className="rounded-xl border border-white/8 bg-[#0d1b2e] flex flex-col h-[580px]">
                  <div className="px-6 py-4 border-b border-white/8">
                    <h2 className="font-semibold">Customer messages</h2>
                    {selectedProject && <p className="text-xs text-white/40 mt-0.5">{selectedProject.reference} · {selectedProject.customerName}</p>}
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {!messages?.length ? (
                      <div className="text-center py-16">
                        <MessageSquare className="h-10 w-10 text-white/20 mx-auto mb-3" />
                        <p className="text-white/40 text-sm">No messages yet.</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isMe = msg.senderRole === "installer";
                        return (
                          <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${isMe ? "bg-brand-lime text-[#050d1a]" : "bg-[#132035] text-white"}`}>
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${isMe ? "text-[#050d1a]/60" : "text-white/40"}`}>
                                {formatRelativeTime(msg.createdAt)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="border-t border-white/8 p-4 flex gap-3">
                    <Input
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder="Message customer..."
                      className="bg-[#132035] border-white/10 text-white"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey && messageText.trim() && selectedProject) {
                          sendMessageMutation.mutate(messageText.trim(), {
                            onSuccess: () => { setMessageText(""); refetchMessages(); },
                            onError: () => toast.error("Failed to send message"),
                          });
                        }
                      }}
                    />
                    <Button
                      className="btn-cta shrink-0"
                      disabled={!messageText.trim() || sendMessageMutation.isPending}
                      onClick={() => {
                        if (messageText.trim() && selectedProject) {
                          sendMessageMutation.mutate(messageText.trim(), {
                            onSuccess: () => { setMessageText(""); refetchMessages(); },
                            onError: () => toast.error("Failed to send message"),
                          });
                        }
                      }}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Documents tab ── */}
            {activeTab === "documents" && (
              <div className="space-y-4">
                {projects && projects.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {projects.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedProjectId(p.id)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                          (selectedProjectId ?? projects[0].id) === p.id
                            ? "border-brand-lime bg-brand-lime/10 text-brand-lime"
                            : "border-white/10 text-white/50 hover:border-white/20"
                        }`}
                      >
                        {p.reference}
                      </button>
                    ))}
                  </div>
                )}
                <div className="rounded-xl border border-white/8 bg-[#0d1b2e] overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                    <h2 className="font-semibold">Project documents</h2>
                    <Badge className="bg-white/10 text-white/60">{documents?.length ?? 0} files</Badge>
                  </div>
                  {!documents?.length ? (
                    <div className="text-center py-16">
                      <FileText className="h-10 w-10 text-white/20 mx-auto mb-3" />
                      <p className="text-white/40 text-sm">No documents uploaded yet.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/8">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-colors">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-lime/10">
                            <FileText className="h-5 w-5 text-brand-lime" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{doc.name}</p>
                            <p className="text-xs text-white/40 capitalize">{doc.category} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("en-GB") : '—'}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white/60 hover:text-white"
                            onClick={() => openSignedDocument(doc.id).catch(() => toast.error('Could not open document'))}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Notifications tab ── */}
            {activeTab === "notifications" && (
              <div className="rounded-xl border border-white/8 bg-[#0d1b2e] overflow-hidden">
                <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
                  <h2 className="font-semibold">Notifications</h2>
                  {unreadCount > 0 && <Badge className="bg-brand-lime/10 text-brand-lime border-brand-lime/30">{unreadCount} unread</Badge>}
                </div>
                {!notifications?.length ? (
                  <div className="text-center py-16">
                    <Bell className="h-10 w-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40 text-sm">No notifications yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/8">
                    {notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`flex items-start gap-4 px-6 py-4 cursor-pointer transition-colors hover:bg-white/5 ${n.readAt ? "opacity-50" : ""}`}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.readAt ? "bg-white/20" : "bg-brand-lime"}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{n.title}</p>
                          <p className="text-xs text-white/40 mt-0.5">{n.body}</p>
                          <p className="text-xs text-white/25 mt-1">{formatRelativeTime(n.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Profile tab ── */}
            {activeTab === "profile" && (
              <InstallerProfileForm profile={profile} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function InstallerAvailabilityPanel({ dates }: { dates: string[] }) {
  const [selected, setSelected] = useState<string[]>(dates);
  const save = useUpdateInstallerAvailability();

  useEffect(() => {
    setSelected(dates);
  }, [dates]);

  const horizonEnd = new Date();
  horizonEnd.setDate(horizonEnd.getDate() + 90);

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="font-display font-semibold text-lg">Installation availability</h2>
        <p className="text-sm text-white/40 mt-1">
          Mark the days you can perform an install. Click a day, or click and drag to select a range. Customers in your coverage area can only pick from these dates, and dated leads only appear here when you are free that day.
        </p>
      </div>
      <div className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6 space-y-4">
        {selected.length === 0 && (
          <p className="text-sm text-white/50">
            No dates selected yet. Add availability so customers can book you and dated leads can match.
          </p>
        )}
        <div className="flex justify-center">
          <InstallAvailabilityCalendar
            mode="multiple"
            selected={selected}
            onSelect={setSelected}
            toDate={horizonEnd}
          />
        </div>
        <p className="text-xs text-white/40">{selected.length} day{selected.length === 1 ? "" : "s"} selected</p>
        <Button
          className="btn-cta"
          disabled={save.isPending}
          onClick={() => {
            save.mutate(selected, {
              onSuccess: () => toast.success("Availability saved."),
              onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save availability."),
            });
          }}
        >
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save availability
        </Button>
      </div>
    </div>
  );
}

// ─── Installer Profile Form ───────────────────────────────────────────────────
function emptyProfileForm() {
  return {
    companyName: "",
    mcsNumber: "",
    reccNumber: "",
    coveragePostcodes: "",
  };
}

function InstallerProfileForm({ profile }: { profile?: InstallerProfile }) {
  const [form, setForm] = useState(emptyProfileForm);

  useEffect(() => {
    if (!profile) return;
    setForm({
      companyName: profile.companyName ?? "",
      mcsNumber: profile.mcsNumber ?? "",
      reccNumber: profile.reccNumber ?? "",
      coveragePostcodes: profile.coveragePostcodes ?? "",
    });
  }, [profile]);

  const updateProfile = useUpdateInstallerProfile();
  const coverageAreaTokens = parseCoverageTokens(form.coveragePostcodes).filter(isAreaToken);

  return (
    <div className="max-w-3xl space-y-6">
      <div className="rounded-xl border border-white/8 bg-[#0d1b2e] p-6">
        <h2 className="font-display font-semibold text-lg mb-6">Company profile</h2>
        <div className="space-y-4">
          {[
            { key: "companyName" as const, label: "Company name", placeholder: "Solar Solutions Ltd" },
            { key: "mcsNumber" as const, label: "MCS certificate number", placeholder: "MCS/123456" },
            { key: "reccNumber" as const, label: "RECC membership number", placeholder: "RECC-789" },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <Label className="text-white/70 text-sm mb-2 block">{label}</Label>
              <Input
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                className="bg-[#132035] border-white/10 text-white"
                placeholder={placeholder}
              />
            </div>
          ))}
          <div>
            <Label className="text-white/70 text-sm mb-2 block">Coverage postcodes</Label>
            <Input
              value={form.coveragePostcodes}
              onChange={(e) => setForm((f) => ({ ...f, coveragePostcodes: e.target.value }))}
              className="bg-[#132035] border-white/10 text-white"
              placeholder="BS, BA, GL, SN (comma-separated)"
            />
            {coverageAreaTokens.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {coverageAreaTokens.map((area) => (
                  <button
                    type="button"
                    key={area}
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        coveragePostcodes: toggleAreaToken(f.coveragePostcodes, area),
                      }))
                    }
                    className="inline-flex items-center gap-1 rounded-full bg-brand-lime/15 px-2.5 py-0.5 text-xs text-brand-lime hover:bg-brand-lime/25"
                    aria-label={`Remove ${area}`}
                  >
                    {area}
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-3">
              <CoverageAreaMap
                value={form.coveragePostcodes}
                onChange={(coveragePostcodes) => setForm((f) => ({ ...f, coveragePostcodes }))}
              />
            </div>
            <p className="text-xs text-white/35 mt-1.5">
              Click an area on the map or type codes. Area codes (BS, M, SW) match every postcode in that area.
              Full postcodes match the whole district, e.g. BS1 4LS shows all BS1 leads. Save to apply.
            </p>
          </div>
        </div>
        <Button
          className="btn-cta mt-6"
          onClick={() => updateProfile.mutate(form, {
            onSuccess: () => toast.success("Profile updated"),
            onError: () => toast.error("Failed to update profile"),
          })}
          disabled={updateProfile.isPending}
        >
          {updateProfile.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save profile
        </Button>
      </div>
    </div>
  );
}

function AttachSurveyButton({ projectId }: { projectId: number }) {
  const [open, setOpen] = useState(false);
  const { data: surveys } = useInstallerSurveyList(open);
  const { data: suggestions } = useAttachSuggestions(projectId, open);
  const attach = useAttachSurvey(projectId);
  const suggestedIds = new Set((suggestions ?? []).map((item) => item.id));
  const attachable = (surveys ?? []).filter((item) => item.listStatus === "submitted");

  const onAttach = async (surveyId: number) => {
    try {
      await attach.mutateAsync(surveyId);
      toast.success("Survey attached to this quote.");
      setOpen(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not attach this survey.";
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="mt-4 border-white/20 text-white/80">
          <ClipboardList className="h-3.5 w-3.5 mr-1" /> Attach field survey
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0d1b2e] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Attach a submitted survey</DialogTitle>
        </DialogHeader>
        {(suggestions ?? []).length > 0 && (
          <p className="text-xs text-brand-lime">
            Suggested matches for this address — confirm before attaching.
          </p>
        )}
        {attachable.length === 0 ? (
          <p className="text-sm text-white/50">No submitted standalone surveys to attach.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {attachable.map((item: InstallerSurveyListItem) => (
              <div key={item.id} className="rounded-lg border border-white/10 p-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{item.propertyAddress || "Address not set"}</p>
                  <p className="text-xs text-white/40">{item.propertyPostcode}</p>
                  {suggestedIds.has(item.id) && <p className="text-xs text-brand-lime mt-1">Address match</p>}
                </div>
                <Button
                  size="sm"
                  className="btn-cta"
                  disabled={attach.isPending}
                  onClick={() => void onAttach(item.id)}
                >
                  Attach
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InstallerPackPanel({
  projectId,
  reference,
  enabled,
}: {
  projectId: number;
  reference: string;
  enabled: boolean;
}) {
  const { data, isLoading } = useInstallerPack(projectId, enabled);
  const saveNote = useSaveInstallerNote(projectId);
  const [note, setNote] = useState("");
  const [htmlOpen, setHtmlOpen] = useState(false);

  useEffect(() => {
    const existing = data?.exceptions.find((ex) => ex.type === "installer_note")?.note ?? "";
    setNote(existing);
  }, [data]);

  if (!enabled) return null;

  const downloadPdf = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/installer-pack.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!response.ok) throw new Error("download failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `installer-pack-${reference}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the pack PDF");
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-brand-lime/20 bg-brand-lime/5 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-brand-lime">Site survey pack</p>
          <p className="text-sm text-white/70 mt-1">Photos, answers, and customer notes from the remote survey.</p>
        </div>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-white/40" />}
      </div>
      {data && (
        <>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="btn-cta" onClick={() => setHtmlOpen((open) => !open)}>
              <FileText className="h-3.5 w-3.5 mr-1" />
              {htmlOpen ? "Hide pack" : "View pack"}
            </Button>
            <Button size="sm" variant="outline" className="border-white/20 text-white/70" onClick={() => void downloadPdf()}>
              <Download className="h-3.5 w-3.5 mr-1" /> PDF
            </Button>
          </div>
          {htmlOpen && (
            <iframe
              title="Installer pack"
              className="w-full h-[480px] rounded-lg bg-white"
              srcDoc={data.html}
            />
          )}
          <div>
            <Label className="text-white/70 text-sm mb-2 block">Note anything that needs follow-up</Label>
            <Textarea
              className="bg-[#132035] border-white/10 text-white"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Button
              size="sm"
              className="btn-cta mt-2"
              disabled={saveNote.isPending}
              onClick={() => {
                saveNote.mutate(note, {
                  onSuccess: () => toast.success("Installer note saved"),
                  onError: () => toast.error("Could not save note"),
                });
              }}
            >
              Save installer note
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
